import { useEffect, useRef, useState } from "react";
import { Lock, ImageOff, Loader2 } from "lucide-react";
import Hls from "hls.js";
import { api, toApiPath } from "@/lib/api";

const NATIVE_HLS = typeof document !== "undefined" && document.createElement("video").canPlayType("application/vnd.apple.mpegurl") !== "";
const USE_HLSJS = !NATIVE_HLS && Hls.isSupported();

// Resolves access-controlled media: the backend authorizes first, then returns either a
// short-lived Supabase signed URL, or (legacy storage) the bytes are fetched as a blob.
async function resolveMedia(url) {
  const path = toApiPath(url);
  const { data } = await api.get(`${path}/access`);
  if (data.provider === "supabase") return { src: data.url, revoke: false };
  if (data.provider === "cloudflare_stream") return { src: data.url, revoke: false, hls: true };
  const r = await api.get(path, { responseType: "blob" });
  return { src: URL.createObjectURL(r.data), revoke: true };
}

function useMediaSrc(url) {
  const [src, setSrc] = useState(null);
  const [state, setState] = useState("loading");
  const [nonce, setNonce] = useState(0);
  const [hls, setHls] = useState(false);
  useEffect(() => {
    if (!url) {
      setState("error");
      return;
    }
    let active = true;
    let made;
    resolveMedia(url)
      .then((m) => {
        if (!active) return;
        made = m;
        setSrc(m.src);
        setHls(!!m.hls);
        setState("ready");
      })
      .catch((e) => active && setState(e.response?.status === 409 ? "processing" : "error"));
    return () => {
      active = false;
      if (made?.revoke) URL.revokeObjectURL(made.src);
    };
  }, [url, nonce]);
  return { src, state, hls, refresh: () => setNonce((n) => n + 1) };
}

export function MediaImage({ url, alt = "", className = "", rounded = "" }) {
  const { src, state } = useMediaSrc(url);

  if (state === "loading")
    return (
      <div className={`flex items-center justify-center bg-white/5 ${className} ${rounded}`}>
        <Loader2 className="animate-spin text-[#6f6878]" size={20} />
      </div>
    );
  if (state === "error")
    return (
      <div className={`flex items-center justify-center bg-white/5 ${className} ${rounded}`}>
        <ImageOff className="text-[#6f6878]" size={20} />
      </div>
    );
  return <img src={src} alt={alt} className={`${className} ${rounded}`} />;
}

export function MediaVideo({ url, poster, className = "" }) {
  const { src, state, hls, refresh } = useMediaSrc(url);
  const ref = useRef(null);
  // Adaptive HLS: Safari/iOS play natively; other browsers use hls.js.
  useEffect(() => {
    const v = ref.current;
    if (!hls || !src || !v || !USE_HLSJS) {
      return undefined;
    }
    const h = new Hls();
    h.loadSource(src);
    h.attachMedia(v);
    return () => { h.destroy(); };
  }, [hls, src, state]);
  const nativeSrc = hls && USE_HLSJS ? undefined : src;
  const resumeAt = useRef(0);
  const retried = useRef(0);
  // Signed URLs expire: on a playback error, get a fresh authorized URL and resume.
  const onError = () => {
    if (!src || src.startsWith("blob:") || retried.current > 2) return;
    retried.current += 1;
    resumeAt.current = ref.current?.currentTime || 0;
    refresh();
  };
  const onLoaded = () => {
    if (resumeAt.current && ref.current) ref.current.currentTime = resumeAt.current;
  };

  if (state === "processing")
    return (
      <div data-testid="media-video-processing" className={`flex flex-col items-center justify-center gap-2 bg-black/60 text-xs text-[#c7c1cf] ${className}`}>
        <Loader2 className="animate-spin text-[#a03cff]" size={22} /> Video is processing — check back shortly.
      </div>
    );
  if (state === "error")
    return (
      <div className={`flex items-center justify-center bg-black/60 ${className}`}>
        <ImageOff className="text-[#6f6878]" size={24} />
      </div>
    );
  if (state === "loading")
    return (
      <div className={`flex items-center justify-center bg-black/60 ${className}`}>
        <Loader2 className="animate-spin text-[#6f6878]" size={24} />
      </div>
    );
  return (
    <video ref={ref} src={nativeSrc} poster={poster} controls playsInline preload="metadata" onError={onError} onLoadedMetadata={onLoaded} className={className} data-testid="media-video" />
  );
}

export function LockedOverlay({ label = "Members Only" }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/55 backdrop-blur-[3px]">
      <Lock size={22} className="text-white" />
      <span className="text-[0.6rem] font-semibold uppercase tracking-wider text-white/90">{label}</span>
    </div>
  );
}
